// Accordion controller
// Handles expand/collapse with smooth transitions
// Adapted from railsblocks.com/docs/accordion
//
// Works in two modes:
// 1. Full AccordionComponent (with trigger + content + icon targets)
// 2. Standalone (data-controller on a wrapper, content uses "hidden" class)
//
// Keyboard: Enter/Space toggle (native button), Escape closes
//
// staticFrom (CONEK-8775): a media query from which the section is always
// expanded (Ui::CardComponent `collapsible: :below_lg`). The CSS forces the
// content visible there; while the query matches this controller ignores
// toggle/Escape, reports the trigger as expanded and takes it out of the tab
// order. Below the query it behaves exactly as without the value.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "content", "icon"]
  static values = {
    open: Boolean,
    // Rotation applied to the icon when open. Defaults to a full flip, which
    // suits the down-chevron used by AccordionComponent (▼ → ▲). Triggers that
    // start from a right-chevron (►) should pass "rotate-90" so it settles at ▼.
    rotate: { type: String, default: "rotate-180" },
    staticFrom: String,
  }

  connect() {
    if (this.openValue) {
      // Start open WITHOUT a max-height cap or overflow clip. The animated
      // open() fixes maxHeight to scrollHeight and relies on overflow-hidden to
      // clip the reveal, but once open both must be released: content that
      // grows after connect (a Tom Select value wrapping to two lines) would be
      // clipped, and an open dropdown/popover escaping the box would be cut off
      // by overflow-hidden. An uncapped, overflow-visible start fixes both; the
      // entry animation only matters on toggle.
      const content = this.contentTarget
      content.classList.remove("hidden")
      content.style.maxHeight = "none"
      content.style.overflow = "visible"
      if (this.hasTriggerTarget)
        this.triggerTarget.setAttribute("aria-expanded", "true")
      if (this.hasIconTarget) this.iconTarget.classList.add(this.rotateValue)
    }

    // Escape key closes
    this._escapeHandler = this._handleEscape.bind(this)
    document.addEventListener("keydown", this._escapeHandler)

    if (this.staticFromValue) {
      this._staticQuery = window.matchMedia(this.staticFromValue)
      this._staticHandler = () => this._syncStatic()
      this._staticQuery.addEventListener("change", this._staticHandler)
      this._syncStatic()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this._escapeHandler)
    if (this._staticQuery) {
      this._staticQuery.removeEventListener("change", this._staticHandler)
      this._staticQuery = null
    }
  }

  toggle() {
    if (this._isStatic()) return
    if (this.openValue) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.openValue = true
    const content = this.contentTarget
    content.classList.remove("hidden")
    // Force reflow so scrollHeight is accurate after removing hidden
    void content.offsetHeight
    content.style.maxHeight = content.scrollHeight + "px"
    if (this.hasTriggerTarget)
      this.triggerTarget.setAttribute("aria-expanded", "true")
    if (this.hasIconTarget) this.iconTarget.classList.add(this.rotateValue)

    // Once the expand animation settles, release the cap AND the overflow clip
    // so later content growth (async widgets, filters) isn't clipped and an
    // open dropdown/popover can escape the box.
    const release = () => {
      if (this.openValue) {
        content.style.maxHeight = "none"
        content.style.overflow = "visible"
      }
    }
    content.addEventListener("transitionend", release, { once: true })
    setTimeout(release, 300)

    this.element.dispatchEvent(
      new CustomEvent("accordion:open", { bubbles: true }),
    )
  }

  close() {
    this.openValue = false
    const content = this.contentTarget

    // Collapse maxHeight for animated close
    content.style.maxHeight = content.scrollHeight + "px"
    void content.offsetHeight
    content.style.maxHeight = "0"
    content.style.overflow = "hidden"

    if (this.hasTriggerTarget)
      this.triggerTarget.setAttribute("aria-expanded", "false")
    if (this.hasIconTarget) this.iconTarget.classList.remove(this.rotateValue)

    // Re-add hidden after collapse
    const cleanup = () => {
      if (!this.openValue) {
        content.classList.add("hidden")
        content.style.maxHeight = ""
        content.style.overflow = ""
      }
    }
    content.addEventListener("transitionend", cleanup, { once: true })
    // Fallback for elements without CSS transition
    setTimeout(cleanup, 300)

    this.element.dispatchEvent(
      new CustomEvent("accordion:close", { bubbles: true }),
    )
  }

  // Recompute the open height after the content changed while expanded
  // (e.g. items shown/hidden by an external filter). No-op when closed.
  refresh() {
    if (this.openValue) {
      const content = this.contentTarget
      content.style.maxHeight = content.scrollHeight + "px"
    }
  }

  _isStatic() {
    return Boolean(this._staticQuery && this._staticQuery.matches)
  }

  // Trigger state for the current viewport: always expanded and out of the
  // tab order while static, the real open state otherwise.
  _syncStatic() {
    if (!this.hasTriggerTarget) return
    if (this._isStatic()) {
      this.triggerTarget.setAttribute("aria-expanded", "true")
      this.triggerTarget.setAttribute("tabindex", "-1")
    } else {
      this.triggerTarget.setAttribute("aria-expanded", String(this.openValue))
      this.triggerTarget.removeAttribute("tabindex")
    }
  }

  _handleEscape(event) {
    if (
      !this._isStatic() &&
      event.key === "Escape" &&
      this.openValue &&
      this.element.contains(document.activeElement)
    ) {
      this.close()
      // Return focus to trigger
      if (this.hasTriggerTarget) {
        this.triggerTarget.focus()
      }
    }
  }
}
